import React from 'react';
import styled from 'styled-components';

import { IS_DEBUG_ENVIRONMENT } from 'config';

const base = '/static/images/coins';

// Note: We are not importing directly because it cause issues with rendering PnLShare to canvas
const unknownIcon = '/static/images/coins/unknown_coin.svg';

/**
 * All coin icons we support
 * Must be located in "/public/static/images/coins"
 */
const coinImages = {
  png: [
    '1000PEPE',
    'ADA',
    'BGT',
    'BIFI',
    'BTC',
    'CRV',
    'DAI',
    'DG',
    'ETH',
    'FRAX',
    'FARTCOIN',
    'FXS',
    'GHST',
    'KNC',
    'LINK',
    'MAI',
    'MANA',
    'POL',
    'PYTH',
    'PENGU',
    'QUICK',
    'QI',
    'SX',
    'SPX',
    'SUSHI',
    'SUI',
    'SYN',
    'TAO',
    'TRUMP',
    'TON',
    'USDC',
    'USDT',
    'UNI',
    'WBTC',
    'WIF',
    'XRP',
    'dev-CENT',
    'dev-DIL',
    'WLD',
  ],
  webp: ['1000BONK', 'AAVE', 'CVX', 'PUMP'],
  svg: [
    'ARB',
    'AVAX',
    'BERA',
    'BNB',
    'DOGE',
    'ENA',
    'ETHFI',
    'IDEX',
    'KAT',
    'LDO',
    'LTC',
    'POPCAT',
    'SOL',
    'SEI',
    'TIA',
  ],
};

/**
 * Assets source (2024-10+)
 *  https://cryptologos.cc/
 *
 * Polygon assets
 *  https://github.com/trustwallet/assets/tree/master/blockchains/polygon
 *
 * Ethereum assets
 *  https://github.com/trustwallet/assets/tree/master/blockchains/ethereum
 *
 * Alternative Repo
 *  https://github.com/Uniswap/assets/tree/master/blockchains/ethereum
 *
 * Alternative https://etherscan.io/token/images/idex_32.png
 *
 * https://assets.coingecko.com/coins/images/15585/small/convex.png
 */
// TODO: move to separate file
export function getCoinIcon(symbol: string) {
  /** We might want to use different icons for test tokens */
  const devSymbol = IS_DEBUG_ENVIRONMENT ? `dev-${symbol}` : undefined;

  if (devSymbol) {
    if (coinImages.png.includes(devSymbol)) {
      return `${base}/${devSymbol}.png`;
    }
    if (coinImages.webp.includes(devSymbol)) {
      return `${base}/${devSymbol}.webp`;
    }
    if (coinImages.svg.includes(devSymbol)) {
      return `${base}/${devSymbol}.svg`;
    }
  }

  if (coinImages.png.includes(symbol)) {
    return `${base}/${symbol}.png`;
  }
  if (coinImages.webp.includes(symbol)) {
    return `${base}/${symbol}.webp`;
  }
  if (coinImages.svg.includes(symbol)) {
    return `${base}/${symbol}.svg`;
  }
  return unknownIcon;
}

// import unknown from './coin/unknown.svg';

// /** https://stackoverflow.com/questions/42118296/dynamically-import-images-from-a-directory-using-webpack */
// const importAllImages = (
//   requireContext: __WebpackModuleApi.RequireContext
// ): {
//   [symbol: string]: string | undefined;
// } =>
//   requireContext.keys().reduce((acc: any, next: any) => {
//     const key = next
//       .replace('./', '')
//       .replace('.png', '')
//       .replace('.svg', '')
//       .replace('.jpg', '')
//       .replace('.jpeg', '')
//       .toUppercase();
//     acc[key] = require(next);
//     return acc;
//   }, {});

// const tokenImages = importAllImages(
//   require.context('./coin', false, /\.(png|jpe?g|svg)$/)
// );

// export function getCoinIcon(symbol: string): string {
//   const found = tokenImages[symbol];
//   if (found && typeof found === 'string') {
//     return found;
//   }
//   return '';
//   // return unknown;
// }

const Image = styled.img<{ $margin?: string; $minWidth: string }>`
  vertical-align: middle;
  height: ${({ height }) => height};
  min-width: ${({ $minWidth }) => $minWidth};
  ${({ $margin }) => ($margin ? `margin: ${$margin};` : '')};
`;

export function CoinIcon({
  symbol,
  size = '24px',
  margin,
}: {
  symbol: string;
  size?: string;
  margin?: string;
}) {
  return (
    <Image
      src={getCoinIcon(symbol)}
      alt={symbol}
      height={size}
      $minWidth={size}
      $margin={margin}
    />
  );
}

/** Optimized Coin Icon for usage in tables */
export function TableCoinIcon({ symbol }: { symbol?: string }) {
  return symbol ? (
    <CoinIcon symbol={symbol} size="12px" margin="-5px 4px 0 0" />
  ) : null;
}

/** Optimized Coin Icon for usage in Mobile data pairs (Details) */
export function MobileDataPairCoinIcon({ symbol }: { symbol?: string }) {
  return symbol ? (
    <CoinIcon symbol={symbol} size="12px" margin="-3px 4px 0 0" />
  ) : null;
}

// Old way - cause to generate lot of imports in dev-server
// import AAVE from './images/AAVE.png?url';
// import BIFI from './images/BIFI.png?url';
// import BNB from './images/BNB.png?url';
// import CRV from './images/CRV.png?url';
// import CVX from './images/CVX.webp?url';
// import DAI from './images/DAI.png?url';
// import ETH from './images/ETH.png?url?url';
// import FRAX from './images/FRAX.png';
// import FXS from './images/FXS.png?url';
// import GHST from './images/GHST.png?url';
// import IDEX from './images/IDEX.svg?url';
// import LINK from './images/LINK.png?url';
// import MAI from './images/MAI.png?url';
// import MANA from './images/MANA.png?url';
// import MATIC from './images/MATIC.png?url';
// import SX from './images/SX.png?url';
// import SYN from './images/SYN.png?url';
// import USDC from './images/USDC.png?url';
// import USDT from './images/USDT.png?url';
// import WBTC from './images/WBTC.png?url';
// import QUICK from './images/QUICK.png?url';
// import SUSHI from './images/SUSHI.png?url';
// import KNC from './images/KNC.png?url';
// import UNI from './images/UNI.png?url';
// import QI from './images/QI.png?url';
// import DG from './images/DG.png?url';
// switch (symbol) {
//   case 'AAVE':
//     return AAVE;
//   case 'BIFI':
//     return BIFI;
//   case 'BNB':
//   case 'WBNB':
//     return BNB;
//   case 'CRV':
//     return CRV;
//   case 'CVX':
//     return CVX;
//   case 'DAI':
//     return DAI;
//   case 'FRAX':
//     return FRAX;
//   case 'FXS':
//     return FXS;
//   case 'ETH':
//   case 'WETH':
//     return ETH;
//   case 'GHST':
//     return GHST;
//   case 'IDEX':
//     return IDEX;
//   case 'LINK':
//     return LINK;
//   case 'MAI':
//     return MAI;
//   case 'MANA':
//     return MANA;
//   case 'MATIC':
//     return MATIC;
//   case 'SX':
//     return SX;
//   case 'SYN':
//     return SYN;
//   case 'USDT':
//     return USDT;
//   case 'USDC':
//     return USDC;
//   case 'WBTC':
//   case 'BTC':
//     return WBTC;
//   case 'QUICK':
//     return QUICK;
//   case 'SUSHI':
//     return SUSHI;
//   case 'KNC':
//     return KNC;
//   case 'UNI':
//     return UNI;
//   case 'QI':
//     return QI;
//   case 'DG':
//     return DG;
//   default:
//     return unknown;
// }
// }
